// src/ordenes/OrdenAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SaveIcon from "@mui/icons-material/Save";

export const OrdenAdmin = () => {
  const toast = useToast();

  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);

  const [loading, setLoading] = useState(true);

  // filtros
  const [filtroClienteId, setFiltroClienteId] = useState("");
  const [mostrarFinalizadas, setMostrarFinalizadas] = useState(true);
  const [mostrarCanceladas, setMostrarCanceladas] = useState(true);
  const [mostrarEliminadas, setMostrarEliminadas] = useState(false);

  // modales
  const [modalEstado, setModalEstado] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);

  // estado OT
  const [nuevoEstado, setNuevoEstado] = useState("pendiente");

  // edición
  const [editTitulo, setEditTitulo] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editCliente, setEditCliente] = useState("");
  const [editCentrosIds, setEditCentrosIds] = useState([]);

  const [loadingCentros, setLoadingCentros] = useState(false);
  const [savingEstado, setSavingEstado] = useState(false);
  const [savingEdicion, setSavingEdicion] = useState(false);

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTodo = async () => {
    setLoading(true);
    try {
      const [resClientes, resOrdenes] = await Promise.all([
        fetch(`${Global.url}clientes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}ordenes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resClientes.ok) {
        toast.error("No se pudieron cargar los clientes.");
        setClientes([]);
      } else {
        const data = await resClientes.json().catch(() => []);
        setClientes(Array.isArray(data) ? data : []);
      }

      if (!resOrdenes.ok) {
        toast.error("No se pudieron cargar las órdenes.");
        setOrdenes([]);
      } else {
        const data = await resOrdenes.json().catch(() => []);
        setOrdenes(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error cargando órdenes:", err);
      toast.error("Error de conexión al cargar órdenes.");
      setOrdenes([]);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Centros por cliente (para modal editar)
  // =========================
  useEffect(() => {
    if (!editCliente) {
      setCentros([]);
      setEditCentrosIds([]);
      return;
    }
    fetchCentros(editCliente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCliente]);

  const fetchCentros = async (clienteId) => {
    setLoadingCentros(true);
    setCentros([]);
    try {
      const res = await fetch(`${Global.url}centros/cliente/${clienteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) {
        toast.error("No se pudieron cargar los centros del cliente.");
        setCentros([]);
        return;
      }
      const data = await res.json().catch(() => []);
      setCentros(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al obtener centros:", err);
      toast.error("Error de conexión al cargar centros.");
      setCentros([]);
    } finally {
      setLoadingCentros(false);
    }
  };

  // =========================
  // Helpers
  // =========================
  const getEstadoNorm = (e) => String(e || "").toLowerCase().trim();

  const clienteOptions = useMemo(() => {
    return (clientes || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes]);

  const clienteFiltroObj =
    clienteOptions.find((c) => String(c.id) === String(filtroClienteId)) || null;

  // =========================
  // Filtrado + rows
  // =========================
  const ordenesFiltradas = useMemo(() => {
    return (ordenes || []).filter((ot) => {
      const matchCliente = !filtroClienteId || Number(ot.cliente_id) === Number(filtroClienteId);

      const estado = getEstadoNorm(ot.estado);
      const visibleFinalizada = mostrarFinalizadas || estado !== "finalizada";
      const visibleCancelada = mostrarCanceladas || estado !== "cancelada";
      const visibleActiva = mostrarEliminadas || ot.activo !== false;

      return matchCliente && visibleFinalizada && visibleCancelada && visibleActiva;
    });
  }, [ordenes, filtroClienteId, mostrarFinalizadas, mostrarCanceladas, mostrarEliminadas]);

  const rows = useMemo(() => {
    return ordenesFiltradas.map((ot) => ({
      ...ot,
      id: ot.id,
      cliente_txt: ot.cliente_nombre || `Cliente ${ot.cliente_id ?? "-"}`,
      estado_txt: ot.estado || "-",
      activo_txt: ot.activo === false ? "Eliminada" : "Activa",
    }));
  }, [ordenesFiltradas]);

  // =========================
  // Acciones
  // =========================
  const abrirModalEstado = (ot) => {
    setOrdenSeleccionada(ot);
    setNuevoEstado(getEstadoNorm(ot.estado) || "pendiente");
    setModalEstado(true);
  };

  const cerrarModalEstado = () => {
    if (savingEstado) return;
    setModalEstado(false);
    setOrdenSeleccionada(null);
    setNuevoEstado("pendiente");
  };

  const cambiarEstado = async () => {
    if (!ordenSeleccionada?.id) return;
    if (!nuevoEstado) return;

    setSavingEstado(true);
    try {
      const res = await fetch(`${Global.url}ordenes/estado/${ordenSeleccionada.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ estado: nuevoEstado }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "No se pudo actualizar el estado.");
        return;
      }

      toast.success("Estado actualizado.");
      cerrarModalEstado();
      await cargarTodo();
    } catch (err) {
      console.error("Error al actualizar estado:", err);
      toast.error("Error de conexión al actualizar el estado.");
    } finally {
      setSavingEstado(false);
    }
  };

  const eliminarOrden = async (id) => {
    if (!window.confirm("¿Eliminar esta orden de trabajo?")) return;

    try {
      const res = await fetch(`${Global.url}ordenes/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo eliminar la orden.");
        return;
      }

      toast.success("Orden eliminada.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al eliminar orden:", err);
      toast.error("Error de conexión al eliminar la orden.");
    }
  };

  const restaurarOrden = async (id) => {
    try {
      const res = await fetch(`${Global.url}ordenes/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ activo: true }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo restaurar la orden.");
        return;
      }

      toast.success("Orden restaurada.");
      await cargarTodo();
    } catch (err) {
      console.error("Error al restaurar orden:", err);
      toast.error("Error de conexión al restaurar la orden.");
    }
  };

  const abrirModalEditar = async (ot) => {
    setOrdenSeleccionada(ot);
    setEditTitulo(ot.titulo || "");
    setEditDescripcion(ot.descripcion || "");
    setEditCliente(ot.cliente_id ? String(ot.cliente_id) : "");
    setEditCentrosIds([]);

    // cargar centros asociados de la orden
    try {
      const res = await fetch(`${Global.url}ordenes/${ot.id}/centros`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json().catch(() => []);
      const ids = (Array.isArray(data) ? data : []).map((c) => c.id);
      setEditCentrosIds(ids);
    } catch (err) {
      console.error("Error al obtener centros asociados:", err);
      setEditCentrosIds([]);
    }

    setModalEditar(true);
  };

  const cerrarModalEditar = () => {
    if (savingEdicion) return;
    setModalEditar(false);
    setOrdenSeleccionada(null);
    setEditTitulo("");
    setEditDescripcion("");
    setEditCliente("");
    setEditCentrosIds([]);
    setCentros([]);
  };

  const guardarCambios = async () => {
    if (!ordenSeleccionada?.id) return;

    if (!editCliente || !editTitulo.trim() || !editDescripcion.trim() || !editCentrosIds.length) {
      toast.warning("Cliente, título, descripción y al menos un centro son obligatorios.");
      return;
    }

    setSavingEdicion(true);
    try {
      const res = await fetch(`${Global.url}ordenes/${ordenSeleccionada.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          cliente_id: editCliente,
          titulo: editTitulo.trim(),
          descripcion: editDescripcion.trim(),
          centros_id: editCentrosIds,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "No se pudo actualizar la orden.");
        return;
      }

      toast.success("Orden actualizada.");
      cerrarModalEditar();
      await cargarTodo();
    } catch (err) {
      console.error("Error al guardar cambios:", err);
      toast.error("Error de conexión al actualizar la orden.");
    } finally {
      setSavingEdicion(false);
    }
  };

  // =========================
  // DataGrid columns
  // =========================
  const columns = useMemo(
    () => [
      { field: "cliente_txt", headerName: "Cliente", flex: 1, minWidth: 180 },
      { field: "titulo", headerName: "Título", flex: 1.2, minWidth: 220 },
      { field: "descripcion", headerName: "Descripción", flex: 1.6, minWidth: 260 },
      { field: "estado_txt", headerName: "Estado", minWidth: 140 },
      {
        field: "acciones",
        headerName: "Acciones",
        sortable: false,
        minWidth: 320,
        renderCell: (params) => {
          const ot = params.row;
          const eliminado = ot.activo === false;

          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%", width: "100%" }}>
              {eliminado ? (
                <Button
                  size="small"
                  startIcon={<RestoreIcon />}
                  onClick={() => restaurarOrden(ot.id)}
                >
                  Restaurar
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => abrirModalEstado(ot)}
                  >
                    Estado
                  </Button>

                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => abrirModalEditar(ot)}
                  >
                    Modificar
                  </Button>

                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => eliminarOrden(ot.id)}
                  >
                    Eliminar
                  </Button>
                </>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientes, centros, filtroClienteId, mostrarEliminadas, mostrarFinalizadas, mostrarCanceladas]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Órdenes de Trabajo
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Stack
            spacing={2}
            direction={{ xs: "column", md: "row" }}
            sx={{ alignItems: { md: "center" } }}
          >
            <Autocomplete
              size="small"
              options={clienteOptions}
              getOptionLabel={(c) => c?.nombre || ""}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={clienteFiltroObj}
              onChange={(_, newValue) => setFiltroClienteId(newValue ? String(newValue.id) : "")}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  placeholder="Todos"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ minWidth: 260, flex: 1 }}
              clearOnEscape
            />

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarFinalizadas}
                  onChange={(e) => setMostrarFinalizadas(e.target.checked)}
                />
              }
              label="Mostrar finalizadas"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarCanceladas}
                  onChange={(e) => setMostrarCanceladas(e.target.checked)}
                />
              }
              label="Mostrar canceladas"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={mostrarEliminadas}
                  onChange={(e) => setMostrarEliminadas(e.target.checked)}
                />
              }
              label="Mostrar eliminadas"
            />
          </Stack>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) => (params.row.activo === false ? "row-disabled" : "")}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay órdenes que mostrar con estos filtros."}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
              "& .row-disabled": {
                opacity: 0.55,
              },
            }}
          />
        </Paper>

        {/* =========================
            Dialog: Cambiar estado
           ========================= */}
        <Dialog open={modalEstado} onClose={cerrarModalEstado} fullWidth maxWidth="sm">
          <DialogTitle>Cambiar estado</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {ordenSeleccionada
                  ? `Orden #${ordenSeleccionada.id} · ${ordenSeleccionada.titulo || ""}`
                  : ""}
              </Typography>

              <TextField
                select
                label="Estado"
                value={nuevoEstado}
                onChange={(e) => setNuevoEstado(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="pendiente">Pendiente</option>
                <option value="en curso">En curso</option>
                <option value="finalizada">Finalizada</option>
                <option value="cancelada">Cancelada</option>
              </TextField>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalEstado} disabled={savingEstado}>
              Cancelar
            </Button>
            <Button onClick={cambiarEstado} disabled={savingEstado} startIcon={<SaveIcon />}>
              {savingEstado ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Editar orden
           ========================= */}
        <Dialog open={modalEditar} onClose={cerrarModalEditar} fullWidth maxWidth="md">
          <DialogTitle>Editar orden</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {ordenSeleccionada ? `Orden #${ordenSeleccionada.id}` : ""}
              </Typography>

              <TextField
                label="Título"
                value={editTitulo}
                onChange={(e) => setEditTitulo(e.target.value)}
                required
              />

              <TextField
                label="Descripción"
                value={editDescripcion}
                onChange={(e) => setEditDescripcion(e.target.value)}
                multiline
                minRows={3}
                required
              />

              <Autocomplete
                size="small"
                options={clienteOptions}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={clienteOptions.find((c) => String(c.id) === String(editCliente)) || null}
                onChange={(_, newValue) => setEditCliente(newValue ? String(newValue.id) : "")}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" placeholder="Selecciona un cliente" required />
                )}
                clearOnEscape
              />

              <Autocomplete
                multiple
                size="small"
                options={(centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))}
                getOptionLabel={(c) => c?.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={(centros || []).filter((c) => editCentrosIds.map(String).includes(String(c.id)))}
                onChange={(_, newValues) => setEditCentrosIds(newValues.map((c) => c.id))}
                loading={loadingCentros}
                disabled={!editCliente || loadingCentros}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centros asociados"
                    placeholder={!editCliente ? "Selecciona antes un cliente" : "Selecciona centros"}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCentros ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                clearOnEscape
                noOptionsText={!editCliente ? "Selecciona antes un cliente" : "No hay centros"}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModalEditar} disabled={savingEdicion}>
              Cancelar
            </Button>
            <Button onClick={guardarCambios} disabled={savingEdicion} startIcon={<SaveIcon />}>
              {savingEdicion ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
